<template>
    <div
        v-if="productResponse?.product"
        class="mb-[10px] flex max-h-[50vh] items-center gap-4 overflow-auto border-b border-green-500 pb-4 last:border-none last:pb-0 lg:gap-6">
        <div class="h-[95px] w-[70px] shrink-0">
            <NuxtLink v-if="productUrl" :to="productUrl" :title="linkTitle" @click="invokeCartSidebar()">
                <ShopwareImg class="h-full w-full object-cover" :alt="imageAlt" :src="product.cover?.url" :width="70" />
            </NuxtLink>
            <ShopwareImg
                v-else
                class="h-full w-full object-cover"
                :alt="imageAlt"
                :src="product.cover?.url"
                :width="70" />
        </div>
        <div class="flex w-full flex-col justify-between">
            <div>
                <div class="flex w-full items-start justify-between">
                    <h3 class="font-mori text-xsm mt-[11px] mb-2 leading-none font-semibold">
                        <NuxtLink
                            v-if="productUrl && product.payload?.manufacturerName"
                            :to="productUrl"
                            :title="linkTitle"
                            class="text-inherit no-underline hover:text-green-600 hover:underline"
                            @click="invokeCartSidebar()">
                            {{ product.payload.manufacturerName }}
                        </NuxtLink>
                        <template v-else>{{ product.payload?.manufacturerName }}</template>
                    </h3>
                    <button
                        type="button"
                        class="flex items-center tracking-[0.8px] duration-200 hover:text-green-600"
                        @click="handleDelete({ id: product.id })">
                        <XMarkIcon class="ml-2 size-6 stroke-2" />
                    </button>
                </div>
                <p class="text-xsm mb-2 leading-none">
                    <NuxtLink
                        v-if="productUrl"
                        :to="productUrl"
                        :title="linkTitle"
                        class="text-inherit no-underline hover:text-green-600 hover:underline"
                        @click="invokeCartSidebar()">
                        {{ product.label }}
                    </NuxtLink>
                    <template v-else>{{ product.label }}</template>
                </p>
            </div>
            <div>
                <div class="flex flex-wrap justify-between gap-1">
                    <div>
                        <template v-if="productResponse.product.availableStock > 0">
                            <select
                                class="text-xsm mr-1 bg-green-200 p-px"
                                @change="changeQuantity(product.id, $event)">
                                <template v-for="index in productResponse.product.stock">
                                    <option
                                        v-if="
                                            index <= productResponse.product.maxPurchase ||
                                            !productResponse.product.maxPurchase
                                        "
                                        :selected="index === product.quantity"
                                        :value="index">
                                        {{ index }}x
                                    </option>
                                </template>
                            </select>

                            <template v-for="(options, index) in productResponse.configurator" :key="options.group">
                                <span
                                    v-if="options.translated?.name !== 'Kleur'"
                                    class="text-xsm mr-1 bg-green-200 p-[2px]">
                                    {{ options.translated?.name }}: {{ getSelectedOptions(options, index) }}
                                </span>
                            </template>
                        </template>
                        <template v-else>
                            <p class="text-xsm leading-none lg:text-sm">
                                {{ snippets['headless.cart.notInStock'] }}
                            </p>
                        </template>
                    </div>
                    <div class="c-item-price flex justify-end gap-4">
                        <p
                            class="text-sm font-bold text-black"
                            :class="{ '!font-medium line-through': product.price?.listPrice?.discount }">
                            <Price :price="multiplyByQuantity(product?.price?.listPrice?.price)" />
                        </p>
                        <p
                            class="bg-yellow-500 px-2 text-sm font-bold text-black"
                            v-if="product.price?.listPrice?.discount">
                            <Price :price="product.price.totalPrice" />
                        </p>
                    </div>
                </div>
                <div class="bg-error-400 my-[12px] gap-4 px-[19px] py-[9px] lg:mt-0" v-if="hasError">
                    <p
                        class="text-error-500 flex items-center gap-2 text-sm leading-[18px] font-light tracking-[0.7px]">
                        <ExclamationCircleIcon class="mr-2.5 size-6 stroke-2" />
                        {{ snippets['headless.cart.productOutOfStockErrorMessage'] }}
                    </p>
                </div>
            </div>
        </div>
    </div>
    <div
        v-else
        class="mb-[10px] flex items-center gap-4 border-b border-green-500 pb-4 last:border-none last:pb-0 lg:gap-6">
        <div class="h-[95px] w-[70px] shrink-0 animate-pulse bg-green-200"></div>
        <div class="flex w-full flex-col gap-2">
            <div class="h-3 w-20 animate-pulse bg-green-200"></div>
            <div class="h-3 w-32 animate-pulse bg-green-200"></div>
            <div class="h-4 w-24 animate-pulse bg-green-200"></div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline';
    import type { Schemas } from '#shopware';

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;
    const cartSidebarStore = useCartSidebarStore();
    const { invokeCartSidebar } = cartSidebarStore;
    const { trackRemoveFromCartFromCartItem } = useGTMEcommerce();
    const { refreshCart, removeItem, changeProductQuantity } = useCart();
    const selectedOptions = ref<string[]>([]);
    const emit = defineEmits(['removeFromCart', 'productLoaded']);
    const props = defineProps<{
        product: Schemas['LineItem'];
        productResponse?: Schemas['ProductDetailResponse'];
        hasError?: boolean;
    }>();

    const productUrl = computed(() => {
        const path = props.productResponse?.product?.seoUrls?.[0]?.seoPathInfo?.toLowerCase();
        return path ? `/${path}` : undefined;
    });
    const linkTitle = computed(() => props.productResponse?.product?.translated?.name ?? props.product.label ?? '');
    const imageAlt = computed(() => props.product.label ?? linkTitle.value ?? '');

    function multiplyByQuantity(price: number): number {
        return price * props.product.quantity;
    }

    function triggerEvent() {
        trackRemoveFromCartFromCartItem(props.product, props.product.quantity);
    }

    async function handleDelete(id) {
        const result = await removeItem(id);
        await refreshCart(result);
        emit('removeFromCart');
        triggerEvent();
    }

    function initializeSelectedOptions() {
        if (!props.product.payload?.options) return;
        selectedOptions.value = [];
        props.product.payload.options.forEach((option, index) => {
            let mappedIndex = index;
            if (index === 1) {
                mappedIndex = 2;
            } else if (index === 2) {
                mappedIndex = 1;
            }
            if (props.product.payload?.optionIds?.[mappedIndex]) {
                selectedOptions.value.push(props.product.payload.optionIds[mappedIndex]);
            }
        });
    }

    initializeSelectedOptions();

    async function changeQuantity(id: string, event) {
        await changeProductQuantity({ id: id, quantity: event.target.value });
        await refreshCart();
    }

    function getSelectedOptions(options, index) {
        const selectedOption = options.options?.find(option => option.id === selectedOptions.value[index]);
        return selectedOption?.translated?.name ?? '';
    }

    onMounted(() => {
        emit('productLoaded');
    });
</script>
